import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useTenants } from "@/components/pages/tenants/hooks/useTenants";
import { CatalogFeature } from "@/types/catalog-feature";
import {
  PLAN_STATUSES,
  PlanFeatureValuePayload,
  PlanStatus,
  SubscriptionPlan,
  UpdateSubscriptionPlanPayload,
} from "@/types/subscription-plan";
import { descriptionToFormString } from "../utils/planFormText";
import { useFeatures } from "../hooks/useFeatures";
import { useUpdateSubscriptionPlan } from "../hooks/useSubscriptionPlanMutations";
import {
  getVisibleCustomPlanFeatureOptions,
  isHiddenCustomPlanFeature,
  isVisibleCustomPlanFeature,
} from "../utils/customPlanFeatureVisibility";
import { PlanFeaturesBadgesField } from "./PlanFeaturesBadgesField";

type EditCustomPlanDialogProps = {
  plan: SubscriptionPlan | null;
  onClose: () => void;
};

const EMPTY_FEATURES: CatalogFeature[] = [];

function buildFeaturePayload(
  ids: string[],
  featureOptions: CatalogFeature[],
  featureValueById: Readonly<Partial<Record<string, string>>>,
): PlanFeatureValuePayload[] {
  const featureById = new Map(
    featureOptions.map((feature) => [feature.id, feature]),
  );

  return ids.map((id) => {
    const feature = featureById.get(id);
    const rawValue =
      feature?.type === "NUMBER"
        ? (featureValueById[id] ?? String(feature.limit ?? "")).trim()
        : (featureValueById[id] ?? "true");

    return {
      featureId: id,
      value: rawValue,
    };
  });
}

export const EditCustomPlanDialog: React.FC<EditCustomPlanDialogProps> = ({
  plan,
  onClose,
}) => {
  const { t } = useTranslation();
  const open = plan !== null;
  const { data: featuresData, isLoading: featuresLoading } = useFeatures(open);
  const features = featuresData ?? EMPTY_FEATURES;
  const { data: tenantsResponse } = useTenants(
    {
      limit: 1000,
      sortBy: "name",
      sortOrder: "asc",
    },
    open,
  );
  const { mutateAsync, isPending } = useUpdateSubscriptionPlan();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [status, setStatus] = useState<PlanStatus>("ACTIVE");
  const [enableCoreFeatures, setEnableCoreFeatures] = useState(false);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [featureValueById, setFeatureValueById] = useState<
    Record<string, string>
  >({});

  const hiddenFeatureIds = useMemo(
    () =>
      new Set(
        features.filter(isHiddenCustomPlanFeature).map((feature) => feature.id),
      ),
    [features],
  );

  useEffect(() => {
    if (plan) {
      const visiblePlanFeatures = plan.features.filter((feature) =>
        isVisibleCustomPlanFeature(feature, hiddenFeatureIds),
      );

      setTitle(plan.title);
      setDescription(descriptionToFormString(plan.description));
      setPriceInput(String(plan.price));
      setCurrency(plan.currency);
      setStatus(
        PLAN_STATUSES.includes(plan.status as PlanStatus)
          ? (plan.status as PlanStatus)
          : "ACTIVE",
      );
      setEnableCoreFeatures(plan.enableCoreFeatures === true);
      setSelectedFeatureIds(visiblePlanFeatures.map((f) => f.featureId));
      const nextFeatureValueById: Record<string, string> = {};
      for (const feature of visiblePlanFeatures) {
        if (typeof feature.value === "string") {
          nextFeatureValueById[feature.featureId] = feature.value;
        }
      }
      setFeatureValueById(nextFeatureValueById);
    }
  }, [hiddenFeatureIds, plan]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plan) {
      return;
    }
    const trimmedTitle = title.trim();
    if (trimmedTitle === "") {
      toast.error(t("subscriptions.plans.custom.edit.validation.title"));
      return;
    }
    const price = Number.parseFloat(priceInput.trim());
    if (!Number.isFinite(price) || price < 0) {
      toast.error(t("subscriptions.plans.custom.edit.validation.price"));
      return;
    }

    const payload: UpdateSubscriptionPlanPayload = {
      title: trimmedTitle,
      price,
      currency: currency.trim() === "" ? "usd" : currency.trim().toLowerCase(),
      status,
      enableCoreFeatures,
      features: buildFeaturePayload(
        selectedFeatureIds,
        featureOptions,
        featureValueById,
      ),
    };

    const descTrimmed = description.trim();
    payload.description = descTrimmed;
    try {
      await mutateAsync({ id: plan.id, payload });
      toast.success(t("subscriptions.plans.custom.edit.successToast"));
      onClose();
    } catch {
      toast.error(t("subscriptions.plans.custom.edit.errorToast"));
    }
  };

  const featureOptions: CatalogFeature[] =
    getVisibleCustomPlanFeatureOptions(features);

  const tenantLabel = useMemo(() => {
    if (!plan || typeof plan.tenantId !== "string") {
      return "—";
    }

    const tenant = tenantsResponse?.items.find(
      (item) => item.id === plan.tenantId,
    );
    if (!tenant) {
      return plan.tenantId;
    }

    return `${tenant.name} (${tenant.slug})`;
  }, [plan, tenantsResponse?.items]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("subscriptions.plans.custom.edit.title")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="text-muted-foreground text-sm">
            {t("subscriptions.plans.custom.edit.tenantId")}:{" "}
            <span className="break-all">{tenantLabel}</span>
          </div>

          <Input
            label={t("subscriptions.plans.custom.edit.planTitle")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoComplete="off"
          />
          <Textarea
            label={t("subscriptions.plans.custom.edit.description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              inputMode="decimal"
              step="any"
              min={0}
              label={t("subscriptions.plans.custom.edit.price")}
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
            />
            <Input
              label={t("subscriptions.plans.custom.edit.currency")}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              maxLength={3}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-foreground text-sm font-medium">
              {t("subscriptions.plans.custom.edit.status")}
            </span>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as PlanStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLAN_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`subscriptions.plans.status.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border-border flex items-center justify-between rounded-lg border px-3 py-2">
            <span className="text-foreground text-sm font-medium">
              {t("subscriptions.plans.featureField.includeCore")}
            </span>
            <Switch
              checked={enableCoreFeatures}
              onCheckedChange={setEnableCoreFeatures}
            />
          </div>

          <PlanFeaturesBadgesField
            featureOptions={featureOptions}
            selectedFeatureIds={selectedFeatureIds}
            onSelectedFeatureIdsChange={setSelectedFeatureIds}
            featuresLoading={featuresLoading}
            featureValueById={featureValueById}
            onFeatureValueByIdChange={setFeatureValueById}
            labels={{
              section: t("subscriptions.plans.custom.edit.features"),
              addPlaceholder: t(
                "subscriptions.plans.custom.edit.selectFeature",
              ),
              removeFeature: t("subscriptions.plans.custom.edit.removeFeature"),
              enabled: t("subscriptions.plans.featureField.enabled"),
              value: t("subscriptions.plans.featureField.value"),
            }}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={onClose}
            >
              {t("subscriptions.plans.custom.edit.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {t("subscriptions.plans.custom.edit.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
